// src/components/settings/CompanyManagement.js
// Phase 1: Company Hierarchy Management with Parent/Subdivision structure
// and assignment of drivers, trucks, and users to parent companies.
// Only Super Admin can manage companies and assignments.

import React, { useEffect, useState } from 'react';
import { db, auth } from '../../firebase';
import { applyOwnerImpersonation } from '../../utils/impersonation';
import { logAudit } from '../../utils/auditLog';
import { withCompanyHistory, getCompanyScopeIds } from '../../utils/companyScope';
import TeamsPanel from './TeamsPanel';
import { teamCompanyId, driverHandovers } from '../../utils/teams';
import { planLoadHandovers, loadHandoverUpdate } from '../../utils/loadHandover';
import { notifyUsers, personMoveNotes, teamMainAdminIds, teamLabel } from '../../utils/notify';
import { useTeamActions } from '../management/useTeamActions';
import {
  collection, query, where, onSnapshot, doc, addDoc, updateDoc, deleteDoc,
  serverTimestamp, getDocs, writeBatch
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const MAX_SUBDIVISIONS = 5;

// A load is "finished" once it's delivered or cancelled — those only matter as
// history. Anything else is live work. Compared lower-cased because status
// casing is inconsistent in older data ("delivered" vs "Delivered").
const COMPLETED_LOAD_STATUSES = ['delivered', 'cancelled', 'canceled'];

const initialCompanyState = {
  name: '',
  address: '',
  phone: '',
  email: '',
  mcNumber: '',
  usdot: '',
  taxId: '',
  logoPath: '',
  logoUrl: '',
};

// ============================================================================
// HELPER: Check if user is Super Admin
// ============================================================================
const isSuperAdmin = (user) => {
  if (!user) return false;
  const roles = Array.isArray(user.role) ? user.role : [user.role].filter(Boolean);
  return roles.includes('Super Admin');
};

// ============================================================================
// HELPER: Detect a "different company" rename vs. a minor correction
// Strips legal suffixes and compares meaningful words — if none overlap at
// all, this reads as renaming to an unrelated company rather than fixing a
// typo/rebrand of the same one.
// ============================================================================
const significantWords = (name) =>
  (name || '')
    .toLowerCase()
    .replace(/\b(llc|inc|corp|corporation|co|ltd|company)\b/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 1);

const looksLikeADifferentCompany = (oldName, newName) => {
  const oldWords = new Set(significantWords(oldName));
  const newWords = new Set(significantWords(newName));
  if (oldWords.size === 0 || newWords.size === 0) return false;
  return ![...oldWords].some(w => newWords.has(w));
};

// ============================================================================
// ASSIGNMENT MODAL COMPONENT
// ============================================================================
const AssignmentModal = ({
  isOpen,
  onClose,
  parentCompany,
  allItems,
  assignedItemIds,
  onSave,
  isSaving,
  title,
  itemLabelKey,
  itemSubLabelKey,
  itemType,
  companyOptions, // Array of {id, name} — parent + its subdivisions (for drivers/trucks only)
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  // Map of itemId -> chosen assignedCompanyId for BOL/invoice branding
  const [bolCompanyMap, setBolCompanyMap] = useState({});
  // Which items had their BOL dropdown explicitly changed THIS session — only
  // these should have their profile company written on save. Everything else
  // in bolCompanyMap is just a starting snapshot, not an intended change.
  const [touchedBolIds, setTouchedBolIds] = useState(new Set());
  // Whether newly added items should also have their BOL / Invoice Company
  // set. This used to be a window.confirm fired AFTER Save, where Cancel
  // did not cancel the save — an easy way to move drivers by accident.
  const [applyBolToNew, setApplyBolToNew] = useState(false);

  const showBolDropdown = itemType === 'drivers' || itemType === 'trucks';

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(assignedItemIds));
      setSearchQuery('');
      setTouchedBolIds(new Set());
      setApplyBolToNew(false);
      // Initialize BOL company map from existing assignedCompanyId on each item
      const initialMap = {};
      allItems.forEach(item => {
        if (assignedItemIds.includes(item.id)) {
          // If item already has an assignedCompanyId that's in our options, use it
          const currentAssigned = item.assignedCompanyId || '';
          const isValidOption = companyOptions?.some(opt => opt.id === currentAssigned);
          initialMap[item.id] = isValidOption ? currentAssigned : (parentCompany?.id || '');
        }
      });
      setBolCompanyMap(initialMap);
    }
  }, [isOpen, assignedItemIds, allItems, companyOptions, parentCompany]);

  if (!isOpen) return null;

  const filteredItems = allItems.filter(item => {
    const label = item[itemLabelKey] || '';
    const subLabel = item[itemSubLabelKey] || '';
    const q = searchQuery.toLowerCase();
    return label.toLowerCase().includes(q) || subLabel.toLowerCase().includes(q);
  });

  const handleToggle = (itemId) => {
    const next = new Set(selectedIds);
    if (next.has(itemId)) {
      next.delete(itemId);
      // Remove from BOL map too
      setBolCompanyMap(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    } else {
      next.add(itemId);
      // Default BOL company to parent
      setBolCompanyMap(prev => ({
        ...prev,
        [itemId]: parentCompany?.id || '',
      }));
    }
    setSelectedIds(next);
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredItems.map(i => i.id);
    const allSelected = allFilteredIds.every(id => selectedIds.has(id));
    const next = new Set(selectedIds);
    const newBolMap = { ...bolCompanyMap };

    if (allSelected) {
      allFilteredIds.forEach(id => {
        next.delete(id);
        delete newBolMap[id];
      });
    } else {
      allFilteredIds.forEach(id => {
        next.add(id);
        if (!newBolMap[id]) {
          newBolMap[id] = parentCompany?.id || '';
        }
      });
    }
    setSelectedIds(next);
    setBolCompanyMap(newBolMap);
  };

  const handleBolCompanyChange = (itemId, companyId) => {
    setBolCompanyMap(prev => ({ ...prev, [itemId]: companyId }));
    setTouchedBolIds(prev => new Set(prev).add(itemId));
  };

  const handleSave = () => {
    onSave(Array.from(selectedIds), bolCompanyMap, Array.from(touchedBolIds), applyBolToNew);
  };

  const addedIds = [...selectedIds].filter(id => !assignedItemIds.includes(id));
  const removedIds = assignedItemIds.filter(id => !selectedIds.has(id));
  const addedCount = addedIds.length;
  const removedCount = removedIds.length;

  // Assigning a driver silently moves their truck to the same company, and
  // un-assigning takes it back out. Surface that before Save rather than
  // letting the truck count change with no explanation.
  const countWithTruck = (ids) => itemType !== 'drivers' ? 0
    : ids.filter(id => allItems.find(i => i.id === id)?.assignedTruckId).length;
  const addedWithTruck = countWithTruck(addedIds);
  const removedWithTruck = countWithTruck(removedIds);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className={`bg-white rounded-lg shadow-xl w-full ${showBolDropdown ? 'max-w-2xl' : 'max-w-lg'} max-h-[85vh] flex flex-col`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            Assign {itemType} to <span className="font-medium text-blue-700">{parentCompany?.name}</span>
          </p>
          {showBolDropdown && (
            <p className="text-xs text-gray-400 mt-1">
              Use the dropdown to choose which company appears on BOL/invoices (parent or subdivision).
            </p>
          )}
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100">
          <input
            type="text"
            placeholder={`Search ${itemType}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Column Headers for drivers/trucks */}
        {showBolDropdown && filteredItems.length > 0 && (
          <div className="px-6 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
            <div className="w-4 flex-shrink-0"></div>
            <div className="flex-1 text-xs font-semibold text-gray-500 uppercase">
              {itemType === 'drivers' ? 'Driver' : 'Truck'}
            </div>
            <div className="w-48 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase">
              BOL / Invoice Company
            </div>
            <div className="w-16 flex-shrink-0"></div>
          </div>
        )}

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-6 py-3">
          {filteredItems.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">
              No {itemType} found{searchQuery ? ' matching search' : ''}
            </p>
          ) : (
            <>
              {/* Select all toggle */}
              <label className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filteredItems.length > 0 && filteredItems.every(i => selectedIds.has(i.id))}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Select all ({filteredItems.length})
                </span>
              </label>

              {filteredItems.map(item => {
                const isSelected = selectedIds.has(item.id);
                const currentParent = item.parentCompanyName || '';
                const isAssignedElsewhere = item.parentCompanyId && item.parentCompanyId !== parentCompany?.id;
                
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 py-2.5 px-2 rounded-md transition-colors ${
                      isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(item.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleToggle(item.id)}>
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {item[itemLabelKey]}
                      </div>
                      {item[itemSubLabelKey] && (
                        <div className="text-xs text-gray-500 truncate">{item[itemSubLabelKey]}</div>
                      )}
                    </div>

                    {/* BOL Company Dropdown — only for drivers/trucks, only when selected */}
                    {showBolDropdown && isSelected && companyOptions && (
                      <select
                        value={bolCompanyMap[item.id] || parentCompany?.id || ''}
                        onChange={(e) => handleBolCompanyChange(item.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-48 flex-shrink-0 border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        {companyOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>
                            {opt.isParent ? `★ ${opt.name}` : `  └ ${opt.name}`}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* Show current parent if assigned elsewhere */}
                    {isAssignedElsewhere && !isSelected && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full flex-shrink-0">
                        {currentParent}
                      </span>
                    )}

                    {item.status && (
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                        item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {item.status}
                      </span>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg space-y-3">
          {/* Consequences, stated before Save rather than after it */}
          {(addedWithTruck > 0 || removedWithTruck > 0) && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-1">
              {addedWithTruck > 0 && (
                <p>
                  <span className="font-medium">Trucks move too:</span> {addedWithTruck} of the drivers
                  being added {addedWithTruck === 1 ? 'has a truck' : 'have trucks'} assigned, so
                  {addedWithTruck === 1 ? ' it' : ' they'} will join this company as well.
                </p>
              )}
              {removedWithTruck > 0 && (
                <p>
                  <span className="font-medium">Trucks leave too:</span> {removedWithTruck} of the drivers
                  being removed {removedWithTruck === 1 ? 'has a truck' : 'have trucks'} assigned, so
                  {removedWithTruck === 1 ? ' it' : ' they'} will be removed from this company as well.
                </p>
              )}
            </div>
          )}

          {showBolDropdown && addedCount > 0 && (
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={applyBolToNew}
                onChange={(e) => setApplyBolToNew(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
              />
              <span className="text-xs text-gray-700">
                Also set the <span className="font-medium">BOL / Invoice Company</span> for
                the {addedCount} being added
                <span className="block text-gray-500">
                  Leave this unticked to keep whatever company already prints on their paperwork.
                </span>
              </span>
            </label>
          )}

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">
              {selectedIds.size} selected
              {addedCount > 0 && <span className="text-green-600 ml-2">+{addedCount} new</span>}
              {removedCount > 0 && <span className="text-red-600 ml-2">-{removedCount} removed</span>}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Assignments'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SUBDIVISION ROW COMPONENT
// ============================================================================
const SubdivisionRow = ({ sub, onEdit, onToggleActive, onDelete, canManage, isDeleting }) => (
  <div className={`flex items-center justify-between py-2 px-3 rounded-md ${
    sub.active ? 'bg-white' : 'bg-gray-100 opacity-70'
  }`}>
    <div className="flex items-center gap-2">
      <span className="text-gray-400 text-xs">└</span>
      <span className="text-sm font-medium text-gray-700">{sub.name}</span>
      {!sub.active && (
        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Inactive</span>
      )}
    </div>
    {canManage && (
      <div className="flex items-center gap-3">
        <button
          onClick={() => onEdit(sub)}
          disabled={isDeleting}
          className="text-indigo-600 hover:text-indigo-800 text-xs font-medium px-2 py-1 rounded hover:bg-indigo-50 disabled:opacity-40"
        >
          Edit
        </button>
        <button
          onClick={() => onToggleActive(sub)}
          disabled={isDeleting}
          className={`text-xs font-medium px-2 py-1 rounded disabled:opacity-40 ${
            sub.active
              ? 'text-yellow-700 hover:text-yellow-900 hover:bg-yellow-50'
              : 'text-green-700 hover:text-green-900 hover:bg-green-50'
          }`}
        >
          {sub.active ? 'Deactivate' : 'Activate'}
        </button>

        {/* Permanent action kept visually apart from the reversible ones */}
        <span className="w-px h-4 bg-gray-300" aria-hidden="true" />

        <button
          onClick={() => onDelete(sub)}
          disabled={isDeleting}
          title="Permanently delete this sub-company"
          className="flex items-center gap-1 text-red-600 hover:text-red-800 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 disabled:opacity-40"
        >
          {isDeleting ? (
            <>
              <span className="inline-block w-3 h-3 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </>
          )}
        </button>
      </div>
    )}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function CompanyManagement() {
  // Auth state (self-managed, same pattern as UserManagement)
  const [loggedInUser, setLoggedInUser] = useState(null);

  // Data state
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Company modal state
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyForm, setCompanyForm] = useState(initialCompanyState);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [companyModalType, setCompanyModalType] = useState('parent'); // 'parent' or 'subdivision'
  const [subdivisionParentId, setSubdivisionParentId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Assignment modal state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentType, setAssignmentType] = useState(null); // 'drivers', 'trucks', 'users'
  const [assignmentParentCompany, setAssignmentParentCompany] = useState(null);
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);
  const [isRepairingHistory, setIsRepairingHistory] = useState(false);
  const [deletingSubId, setDeletingSubId] = useState(null);
  // In-page feedback, so results appear where the user is looking instead
  // of in a browser dialog they have to dismiss.
  const [actionMessage, setActionMessage] = useState(null); // { type, text }
  const [repairResult, setRepairResult] = useState(null);

  // Expanded parent companies
  const [expandedParents, setExpandedParents] = useState(new Set());

  // ============================================================================
  // AUTH LISTENER (same pattern as UserManagement)
  // ============================================================================
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const unsubProfile = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
          if (docSnap.exists()) {
            setLoggedInUser(applyOwnerImpersonation({ uid: user.uid, email: user.email, ...docSnap.data() }));
          } else {
            setLoggedInUser({ uid: user.uid, email: user.email, role: [] });
          }
        });
        return () => unsubProfile();
      } else {
        setLoggedInUser(null);
      }
    });
    return unsubscribeAuth;
  }, []);

  const canManage = isSuperAdmin(loggedInUser);
  const tenantId = loggedInUser?.tenantId;

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  useEffect(() => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Fetch companies
    const companiesQuery = query(
      collection(db, 'companies'),
      where('tenantId', '==', tenantId)
    );
    const unsubCompanies = onSnapshot(companiesQuery, (snapshot) => {
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setCompanies(list);
      setIsLoading(false);
    });

    // Fetch drivers (all, not just active — so we can see full picture)
    const driversQuery = query(
      collection(db, 'drivers'),
      where('tenantId', '==', tenantId)
    );
    const unsubDrivers = onSnapshot(driversQuery, (snapshot) => {
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(d => !d.isDeleted && d.status !== 'Deleted');
      setDrivers(list);
    });

    // Fetch trucks
    const trucksQuery = query(
      collection(db, 'trucks'),
      where('tenantId', '==', tenantId)
    );
    const unsubTrucks = onSnapshot(trucksQuery, (snapshot) => {
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => !t.isDeleted && t.status !== 'Deleted');
      setTrucks(list);
    });

    // Fetch users (non-Super Admin users for assignment)
    const usersQuery = query(
      collection(db, 'users'),
      where('tenantId', '==', tenantId)
    );
    const unsubUsers = onSnapshot(usersQuery, (snapshot) => {
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(list);
    });

    return () => {
      unsubCompanies();
      unsubDrivers();
      unsubTrucks();
      unsubUsers();
    };
  }, [tenantId]);

  // ============================================================================
  // DERIVED DATA
  // ============================================================================
  const allParentCompanies = companies
    .filter(c => !c.parentCompanyId && c.type !== 'subdivision')
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

// Main Admin only sees their assigned companies
const userParentCompanyIds = loggedInUser?.assignedParentCompanyIds || [];
const parentCompanies = canManage 
    ? allParentCompanies 
    : allParentCompanies.filter(c => userParentCompanyIds.includes(c.id));

  const getSubdivisions = (parentId) =>
    companies
      .filter(c => c.parentCompanyId === parentId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const getAssignedDrivers = (parentId) =>
    drivers.filter(d => d.parentCompanyId === parentId);

  const getAssignedTrucks = (parentId) =>
    trucks.filter(t => t.parentCompanyId === parentId);

  const getAssignedUsers = (parentId) =>
    users.filter(u => {
      const ids = u.assignedParentCompanyIds || [];
      return ids.includes(parentId);
    });

  // Count unassigned
  const unassignedDrivers = drivers.filter(d => !d.parentCompanyId);
  const unassignedTrucks = trucks.filter(t => !t.parentCompanyId);
  const unassignedUsers = users.filter(u => {
    if (isSuperAdmin(u)) return false; // Super Admins don't need assignment
    const ids = u.assignedParentCompanyIds || [];
    return ids.length === 0;
  });

  // ============================================================================
  // COMPANY CRUD HANDLERS
  // ============================================================================
  const handleOpenAddParent = () => {
    setCompanyForm(initialCompanyState);
    setIsEditingCompany(false);
    setEditingCompanyId(null);
    setCompanyModalType('parent');
    setSubdivisionParentId(null);
    setShowCompanyModal(true);
  };

  const handleOpenAddSubdivision = (parentId) => {
    const subs = getSubdivisions(parentId);
    if (subs.length >= MAX_SUBDIVISIONS) {
      alert(`Maximum ${MAX_SUBDIVISIONS} subdivisions per parent company.`);
      return;
    }
    setCompanyForm(initialCompanyState);
    setIsEditingCompany(false);
    setEditingCompanyId(null);
    setCompanyModalType('subdivision');
    setSubdivisionParentId(parentId);
    setShowCompanyModal(true);
  };

  const handleOpenEditCompany = (company) => {
    setCompanyForm({
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      mcNumber: company.mcNumber || '',
      usdot: company.usdot || '',
      taxId: company.taxId || '',
      logoPath: company.logoPath || '',
      logoUrl: company.logoUrl || '',
    });
    setIsEditingCompany(true);
    setEditingCompanyId(company.id);
    setCompanyModalType(company.parentCompanyId ? 'subdivision' : 'parent');
    setSubdivisionParentId(company.parentCompanyId || null);
    setShowCompanyModal(true);
  };

  const handleCompanyFormChange = (e) => {
    const { name, value } = e.target;
    setCompanyForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCompanyFormSubmit = async (e) => {
    e.preventDefault();
    if (!companyForm.name?.trim()) {
      alert('Company name is required.');
      return;
    }

    if (isEditingCompany && editingCompanyId) {
      const existingCompany = companies.find(c => c.id === editingCompanyId);
      const newName = companyForm.name.trim();
      if (existingCompany && existingCompany.name !== newName && looksLikeADifferentCompany(existingCompany.name, newName)) {
        const proceed = window.confirm(
          `This looks like renaming "${existingCompany.name}" to a completely different company ("${newName}"), not a spelling fix.\n\n` +
          `Renaming here changes the name shown on past BOLs/invoices too, wherever they use the driver's default company.\n\n` +
          `If "${newName}" is actually a separate company, click Cancel and create it as a NEW company instead, then reassign drivers/trucks to it.\n\n` +
          `Continue renaming "${existingCompany.name}" anyway?`
        );
        if (!proceed) return;
      }
    }

    setIsProcessing(true);
    try {
      if (isEditingCompany && editingCompanyId) {
        // UPDATE existing company
        const companyRef = doc(db, 'companies', editingCompanyId);
        const updateData = {
          name: companyForm.name.trim(),
          address: companyForm.address.trim(),
          phone: companyForm.phone.trim(),
          email: companyForm.email.trim(),
          mcNumber: companyForm.mcNumber.trim(),
          usdot: companyForm.usdot.trim(),
          taxId: companyForm.taxId.trim(),
          updatedAt: serverTimestamp(),
        };
        await updateDoc(companyRef, updateData);

        const oldCompanyForLog = companies.find(c => c.id === editingCompanyId);
        const changes = {};
        for (const field of ['name', 'address', 'phone', 'email', 'mcNumber', 'usdot', 'taxId']) {
          const oldValue = oldCompanyForLog?.[field] || '';
          const newValue = updateData[field];
          if (oldValue !== newValue) changes[field] = { oldValue, newValue };
        }
        if (Object.keys(changes).length > 0) {
          logAudit({
            userId: loggedInUser.uid,
            userEmail: loggedInUser.email,
            action: 'COMPANY_UPDATED',
            targetType: 'company',
            targetId: editingCompanyId,
            details: { companyName: updateData.name, changes },
            tenantId
          });
        }

        // If editing a parent company, update denormalized parentCompanyName on subdivisions, drivers, trucks, loads
        if (companyModalType === 'parent') {
          const oldCompany = companies.find(c => c.id === editingCompanyId);
          if (oldCompany && oldCompany.name !== companyForm.name.trim()) {
            await updateDenormalizedParentName(editingCompanyId, companyForm.name.trim());
          }
        }

        // If editing a subdivision, update denormalized assignedCompanyName on drivers/trucks that use this subdivision
        if (companyModalType === 'subdivision') {
          const oldCompany = companies.find(c => c.id === editingCompanyId);
          if (oldCompany && oldCompany.name !== companyForm.name.trim()) {
            await updateDenormalizedSubdivisionName(editingCompanyId, companyForm.name.trim());
          }
        }

        alert('Company updated successfully!');
      } else {
        // CREATE new company
        const parentCompany = subdivisionParentId
          ? companies.find(c => c.id === subdivisionParentId)
          : null;

        const newCompanyData = {
          name: companyForm.name.trim(),
          address: companyForm.address.trim(),
          phone: companyForm.phone.trim(),
          email: companyForm.email.trim(),
          mcNumber: companyForm.mcNumber.trim(),
          usdot: companyForm.usdot.trim(),
          taxId: companyForm.taxId.trim(),
          logoPath: '',
          logoUrl: '',
          active: true,
          tenantId: tenantId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Hierarchy fields
          type: companyModalType === 'subdivision' ? 'subdivision' : 'parent',
          parentCompanyId: subdivisionParentId || null,
          parentCompanyName: parentCompany?.name || null,
        };

        const newCompanyRef = await addDoc(collection(db, 'companies'), newCompanyData);
        logAudit({
          userId: loggedInUser.uid,
          userEmail: loggedInUser.email,
          action: 'COMPANY_CREATED',
          targetType: 'company',
          targetId: newCompanyRef.id,
          details: {
            companyName: newCompanyData.name,
            usdot: newCompanyData.usdot,
            mcNumber: newCompanyData.mcNumber,
            taxId: newCompanyData.taxId
          },
          tenantId
        });
        alert(`${companyModalType === 'subdivision' ? 'Sub-company' : 'Company'} created successfully!`);
      }

      setShowCompanyModal(false);
      setCompanyForm(initialCompanyState);
    } catch (error) {
      console.error('Error saving company:', error);
      alert('Failed to save company: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleCompanyActive = async (company) => {
    const newActive = !company.active;
    const label = company.parentCompanyId ? 'sub-company' : 'company';

    // Deactivating deliberately does NOT unlink anybody — it is the safe,
    // reversible alternative to deleting, and the delete path tells people to
    // reach for it precisely because records stay intact. But silence is what
    // makes it confusing: a Super Admin hides a company and later finds drivers
    // belonging to something that no longer appears in any list, with nothing
    // explaining why. So the count is shown up front.
    //
    // The two relationships are counted separately because they are different
    // facts: who manages a driver (parentCompanyId) and whose authority their
    // paperwork runs under (assignedCompanyId). A carrier running several
    // authorities will have drivers in one and not the other.
    let impact = '';
    if (!newActive) {
      const belongHere = drivers.filter(d => d.parentCompanyId === company.id);
      const bolHere = drivers.filter(d => d.assignedCompanyId === company.id);
      const trucksHere = trucks.filter(t =>
        t.parentCompanyId === company.id || t.assignedCompanyId === company.id);

      const lines = [];
      if (belongHere.length > 0) {
        lines.push(`• ${belongHere.length} driver${belongHere.length !== 1 ? 's' : ''} belong${belongHere.length === 1 ? 's' : ''} to this company`);
      }
      if (bolHere.length > 0) {
        lines.push(`• ${bolHere.length} driver${bolHere.length !== 1 ? 's' : ''} run${bolHere.length === 1 ? 's' : ''} BOL paperwork under it`);
      }
      if (trucksHere.length > 0) {
        lines.push(`• ${trucksHere.length} truck${trucksHere.length !== 1 ? 's' : ''} linked to it`);
      }

      if (lines.length > 0) {
        impact =
          '\n\n' + lines.join('\n') +
          '\n\nNothing is unlinked — they stay attached and keep working. The company ' +
          'simply stops appearing in lists while it is inactive, so it cannot be picked ' +
          'for new assignments.' +
          '\n\nIf you want these drivers managed somewhere else, move them to another ' +
          'company first.';
      }
    }

    if (!window.confirm(
      `${newActive ? 'Activate' : 'Deactivate'} ${label} "${company.name}"?` +
      impact +
      (!newActive && !company.parentCompanyId ? '\n\nThis will NOT deactivate its sub-companies — do that separately if needed.' : '')
    )) return;

    try {
      await updateDoc(doc(db, 'companies', company.id), {
        active: newActive,
        updatedAt: serverTimestamp(),
      });
      logAudit({
        userId: loggedInUser.uid,
        userEmail: loggedInUser.email,
        action: 'COMPANY_STATUS_TOGGLED',
        targetType: 'company',
        targetId: company.id,
        details: { companyName: company.name, newStatus: newActive ? 'Active' : 'Inactive' },
        tenantId
      });
      alert(`${company.name} ${newActive ? 'activated' : 'deactivated'}.`);
    } catch (error) {
      console.error('Error toggling company:', error);
      alert('Failed to update company: ' + error.message);
    }
  };

  // ============================================================================
  // DENORMALIZATION HELPERS
  // ============================================================================
  const updateDenormalizedParentName = async (parentId, newName) => {
    const batch = writeBatch(db);

    // Update subdivisions
    const subs = companies.filter(c => c.parentCompanyId === parentId);
    subs.forEach(sub => {
      batch.update(doc(db, 'companies', sub.id), { parentCompanyName: newName });
    });

    // Update drivers with this parentCompanyId
    const assignedDrivers = drivers.filter(d => d.parentCompanyId === parentId);
    assignedDrivers.forEach(d => {
      batch.update(doc(db, 'drivers', d.id), { parentCompanyName: newName });
    });

    // Update trucks with this parentCompanyId
    const assignedTrucks = trucks.filter(t => t.parentCompanyId === parentId);
    assignedTrucks.forEach(t => {
      batch.update(doc(db, 'trucks', t.id), { parentCompanyName: newName });
    });

    // Update users — need to update the names array
    const assignedUsers = users.filter(u => (u.assignedParentCompanyIds || []).includes(parentId));
    assignedUsers.forEach(u => {
      const ids = u.assignedParentCompanyIds || [];
      const names = u.assignedParentCompanyNames || [];
      const idx = ids.indexOf(parentId);
      if (idx !== -1) {
        const newNames = [...names];
        newNames[idx] = newName;
        batch.update(doc(db, 'users', u.id), { assignedParentCompanyNames: newNames });
      }
    });

    await batch.commit();
  };

  const updateDenormalizedSubdivisionName = async (subdivisionId, newName) => {
    const batch = writeBatch(db);

    // Update drivers whose assignedCompanyId points to this subdivision
    const affectedDrivers = drivers.filter(d => d.assignedCompanyId === subdivisionId);
    affectedDrivers.forEach(d => {
      batch.update(doc(db, 'drivers', d.id), { assignedCompanyName: newName });
    });

    // Update trucks whose assignedCompanyId points to this subdivision
    const affectedTrucks = trucks.filter(t => t.assignedCompanyId === subdivisionId);
    affectedTrucks.forEach(t => {
      batch.update(doc(db, 'trucks', t.id), { assignedCompanyName: newName });
    });

    await batch.commit();
  };

  // ============================================================================
  // ASSIGNMENT HANDLERS
  // ============================================================================
  const handleOpenAssignment = (type, parentCompany) => {
    setAssignmentType(type);
    setAssignmentParentCompany(parentCompany);
    setShowAssignmentModal(true);
  };

  const handleSaveAssignment = async (selectedIds, bolCompanyMap = {}, touchedBolIds = [], applyBolToNew = false) => {
    if (!assignmentParentCompany || !assignmentType) return;

    const touchedSet = new Set(touchedBolIds);
    let logCounts = { added: 0, removed: 0, profileCompanyChanged: 0 };

    setIsSavingAssignment(true);
    try {
      const parentId = assignmentParentCompany.id;
      const parentName = assignmentParentCompany.name;
      const batch = writeBatch(db);
      const pendingNotes = [];  // sent once the batch has been written
      // A driver leaving a team here: their dispatcher and the team's Main
      // Admins are told. Current loads stay where they are.
      const driverLeftNotes = (driver, where) => {
        if (!driver) return [];
        const name = driver.name || 'A driver';
        return [
          driver.dispatcherId && { userId: driver.dispatcherId,
            message: `Driver ${name} moved to ${where}. You are no longer their dispatcher. Their current loads stay with you.` },
          ...teamMainAdminIds(users, driver.teamId).map(uid => ({ userId: uid,
            message: `Driver ${name} left your team ${teamLabel(companies, driver.teamId)} for ${where}. Any current loads stay with your team.` })),
        ].filter(Boolean);
      };

      if (assignmentType === 'drivers') {
        // Get currently assigned driver IDs for this parent
        const currentlyAssigned = drivers.filter(d => d.parentCompanyId === parentId).map(d => d.id);
        const toAdd = selectedIds.filter(id => !currentlyAssigned.includes(id));
        const toRemove = currentlyAssigned.filter(id => !selectedIds.includes(id));
        const toUpdate = selectedIds.filter(id => currentlyAssigned.includes(id));

        // Being added to the group always changes dispatcher visibility. Whether
        // it should ALSO change the item's BOL / Invoice Company is a separate
        // decision, made deliberately in the modal before Save — never in a
        // dialog after it, where Cancel looked like it would abort the save.
        const alsoSetProfileCompany = toAdd.length > 0 && applyBolToNew;
        logCounts = {
          added: toAdd.length,
          removed: toRemove.length,
          profileCompanyChanged: (alsoSetProfileCompany ? toAdd.length : 0) + toUpdate.filter(id => touchedSet.has(id)).length
        };

        // Add parent company (group membership) to newly selected drivers
        toAdd.forEach(driverId => {
          const bolCompanyId = bolCompanyMap[driverId] || parentId;
          const bolCompany = companies.find(c => c.id === bolCompanyId);
          const driver = drivers.find(d => d.id === driverId);

          const driverUpdate = {
            parentCompanyId: parentId,
            parentCompanyName: parentName,
            // Append-only: the previous company keeps seeing this driver's past work.
            companyHistoryIds: withCompanyHistory(driver, parentId),
            updatedAt: serverTimestamp(),
          };
          // A team belongs to a company: the old company's team, and the
          // dispatcher from it, do not come along. Left behind they made the
          // driver invisible on both companies' Teams screens while a dispatcher
          // from the old team still held them. They arrive in "Not in a team
          // here", to be placed on a team of this company.
          if (teamCompanyId(driver?.teamId) !== parentId) {
            driverUpdate.teamId = null;
            driverUpdate.dispatcherId = null;
            pendingNotes.push(...driverLeftNotes(driver, `company ${parentName}`));
          }
          if (alsoSetProfileCompany) {
            driverUpdate.assignedCompanyId = bolCompanyId;
            driverUpdate.assignedCompanyName = bolCompany?.name || parentName;
          }
          batch.update(doc(db, 'drivers', driverId), driverUpdate);

          // Auto-assign driver's truck to same parent company (group only, same rule)
          if (driver?.assignedTruckId) {
            const truckAlreadyAssigned = trucks.find(t => t.id === driver.assignedTruckId);
            if (truckAlreadyAssigned && truckAlreadyAssigned.parentCompanyId !== parentId) {
              const truckUpdate = {
                parentCompanyId: parentId,
                parentCompanyName: parentName,
                companyHistoryIds: withCompanyHistory(truckAlreadyAssigned, parentId),
                updatedAt: serverTimestamp(),
              };
              if (alsoSetProfileCompany) {
                truckUpdate.assignedCompanyId = bolCompanyId;
                truckUpdate.assignedCompanyName = bolCompany?.name || parentName;
              }
              batch.update(doc(db, 'trucks', driver.assignedTruckId), truckUpdate);
            }
          }
        });

        // Drivers already in the group: only touch their profile company if the
        // operator explicitly changed the BOL dropdown for them THIS session —
        // never re-apply the modal's opening snapshot just because it differs
        // from whatever the driver's profile currently says.
        toUpdate.forEach(driverId => {
          if (!touchedSet.has(driverId)) return;
          const bolCompanyId = bolCompanyMap[driverId];
          if (bolCompanyId) {
            const bolCompany = companies.find(c => c.id === bolCompanyId);
            batch.update(doc(db, 'drivers', driverId), {
              assignedCompanyId: bolCompanyId,
              assignedCompanyName: bolCompany?.name || parentName,
              updatedAt: serverTimestamp(),
            });
          }
        });

        // Remove from group only — leave the driver's profile company alone.
        toRemove.forEach(driverId => {
          const driver = drivers.find(d => d.id === driverId);
          pendingNotes.push(...driverLeftNotes(driver, 'no company'));

          batch.update(doc(db, 'drivers', driverId), {
            parentCompanyId: null,
            parentCompanyName: null,
            // Record the company we're removing them from BEFORE clearing it,
            // otherwise this parent loses sight of the driver's past work.
            companyHistoryIds: withCompanyHistory(driver, parentId),
            // Out of the company means off its teams and away from its
            // dispatchers too.
            teamId: null,
            dispatcherId: null,
            updatedAt: serverTimestamp(),
          });

          if (driver?.assignedTruckId) {
            const truck = trucks.find(t => t.id === driver.assignedTruckId);
            batch.update(doc(db, 'trucks', driver.assignedTruckId), {
              parentCompanyId: null,
              parentCompanyName: null,
              companyHistoryIds: withCompanyHistory(truck, parentId),
              updatedAt: serverTimestamp(),
            });
          }
        });
      } else if (assignmentType === 'trucks') {
        const currentlyAssigned = trucks.filter(t => t.parentCompanyId === parentId).map(t => t.id);
        const toAdd = selectedIds.filter(id => !currentlyAssigned.includes(id));
        const toRemove = currentlyAssigned.filter(id => !selectedIds.includes(id));
        const toUpdate = selectedIds.filter(id => currentlyAssigned.includes(id));

        // Decided in the modal before Save — see the drivers branch above.
        const alsoSetProfileCompany = toAdd.length > 0 && applyBolToNew;
        logCounts = {
          added: toAdd.length,
          removed: toRemove.length,
          profileCompanyChanged: (alsoSetProfileCompany ? toAdd.length : 0) + toUpdate.filter(id => touchedSet.has(id)).length
        };

        toAdd.forEach(truckId => {
          const bolCompanyId = bolCompanyMap[truckId] || parentId;
          const bolCompany = companies.find(c => c.id === bolCompanyId);
          const truckUpdate = {
            parentCompanyId: parentId,
            parentCompanyName: parentName,
            companyHistoryIds: withCompanyHistory(trucks.find(t => t.id === truckId), parentId),
            updatedAt: serverTimestamp(),
          };
          if (alsoSetProfileCompany) {
            truckUpdate.assignedCompanyId = bolCompanyId;
            truckUpdate.assignedCompanyName = bolCompany?.name || parentName;
          }
          batch.update(doc(db, 'trucks', truckId), truckUpdate);
        });

        toUpdate.forEach(truckId => {
          if (!touchedSet.has(truckId)) return;
          const bolCompanyId = bolCompanyMap[truckId];
          if (bolCompanyId) {
            const bolCompany = companies.find(c => c.id === bolCompanyId);
            batch.update(doc(db, 'trucks', truckId), {
              assignedCompanyId: bolCompanyId,
              assignedCompanyName: bolCompany?.name || parentName,
              updatedAt: serverTimestamp(),
            });
          }
        });

        toRemove.forEach(truckId => {
          batch.update(doc(db, 'trucks', truckId), {
            parentCompanyId: null,
            parentCompanyName: null,
            companyHistoryIds: withCompanyHistory(trucks.find(t => t.id === truckId), parentId),
            updatedAt: serverTimestamp(),
          });
        });

      } else if (assignmentType === 'users') {
        // Users have arrays — add/remove this parentId from their arrays
        const currentlyAssigned = users
          .filter(u => (u.assignedParentCompanyIds || []).includes(parentId))
          .map(u => u.id);
        const toAdd = selectedIds.filter(id => !currentlyAssigned.includes(id));
        const toRemove = currentlyAssigned.filter(id => !selectedIds.includes(id));

        toAdd.forEach(userId => {
          const user = users.find(u => u.id === userId);
          const existingIds = user?.assignedParentCompanyIds || [];
          const existingNames = user?.assignedParentCompanyNames || [];
          batch.update(doc(db, 'users', userId), {
            assignedParentCompanyIds: [...existingIds, parentId],
            assignedParentCompanyNames: [...existingNames, parentName],
          });
        });

        for (const userId of toRemove) {
          const user = users.find(u => u.id === userId);
          const existingIds = user?.assignedParentCompanyIds || [];
          const existingNames = user?.assignedParentCompanyNames || [];
          const idx = existingIds.indexOf(parentId);
          if (idx !== -1) {
            const newIds = existingIds.filter((_, i) => i !== idx);
            const newNames = existingNames.filter((_, i) => i !== idx);

            // Leaving a company means leaving its teams. Recorded in
            // formerTeams (as the Teams screen does) so a Main Admin keeps the
            // loads booked before they left and nothing newer.
            const teamIds = Array.isArray(user?.teamIds) ? user.teamIds : [];
            const leftTeams = teamIds.filter(t => teamCompanyId(t) === parentId);
            const update = {
              assignedParentCompanyIds: newIds,
              assignedParentCompanyNames: newNames,
            };
            if (leftTeams.length > 0) {
              const removedAt = new Date().toISOString();
              update.teamIds = teamIds.filter(t => !leftTeams.includes(t));
              update.formerTeams = [
                ...(Array.isArray(user?.formerTeams) ? user.formerTeams : []),
                ...leftTeams.map(t => ({ teamId: t, removedAt, removedBy: loggedInUser?.uid || null })),
              ];
            }
            batch.update(doc(db, 'users', userId), update);

            // Their drivers on those teams are handed over, as on the Teams
            // screen (driverHandovers in utils/teams), and their unfinished
            // loads there follow ("See only" is what this screen records).
            const handovers = driverHandovers({ leavingUserId: userId, leftTeamIds: leftTeams, drivers, users });
            handovers.forEach(h => batch.update(doc(db, 'drivers', h.driverId), {
              dispatcherId: h.dispatcherId,
              updatedAt: serverTimestamp(),
            }));
            const loadHandovers = await planLoadHandovers({
              tenantId, leavingUserId: userId, leftTeamIds: leftTeams, loadAccess: 'view',
              driverHandovers: handovers, users, drivers,
            });
            loadHandovers.forEach(h => batch.update(h.ref, loadHandoverUpdate(h, userId, loggedInUser?.uid)));
            pendingNotes.push(...personMoveNotes({
              user, id: userId, left: leftTeams, access: 'view', handovers, loadHandovers, users, drivers, companies,
            }));
          }
        }
      }

      await batch.commit();
      await notifyUsers({ tenantId, actorId: loggedInUser?.uid, notes: pendingNotes });

      if (assignmentType === 'drivers' || assignmentType === 'trucks') {
        logAudit({
          userId: loggedInUser.uid,
          userEmail: loggedInUser.email,
          action: 'GROUP_ASSIGNMENT_UPDATED',
          targetType: assignmentType === 'drivers' ? 'driverGroup' : 'truckGroup',
          targetId: parentId,
          details: {
            companyName: parentName,
            added: logCounts.added,
            removed: logCounts.removed,
            profileCompanyChanged: logCounts.profileCompanyChanged
          },
          tenantId
        });
      }

      setShowAssignmentModal(false);
      alert('Assignments updated successfully!');
    } catch (error) {
      console.error('Error saving assignments:', error);
      alert('Failed to save assignments: ' + error.message);
    } finally {
      setIsSavingAssignment(false);
    }
  };

  // ============================================================================
  // TEAMS
  // ----------------------------------------------------------------------------
  // Teams live on the company document. Membership lives on the person, so a
  // driver or user is the single source of truth for which team they are on.
  // Nothing here affects access — see utils/teams.js.
  //
  // The three writes themselves live in management/useTeamActions.js, shared
  // with the Teams tab under Management. They used to be spelled out here, but
  // rules this subtle — moving a driver's company when they join another
  // company's team, auto-linking a lone dispatcher — must not exist in two
  // copies. Drift in exactly this area is what made teams look broken before.
  // ============================================================================
  const { teamBusy, saveTeams, assignPersonToTeam, linkDriverToDispatcher } =
    useTeamActions({
      loggedInUser,
      tenantId,
      users,
      drivers,
      companies,
      canManage,
      onError: setActionMessage,
    });

  // ============================================================================
  // DELETE SUBDIVISION
  // ----------------------------------------------------------------------------
  // Only ever offered for subdivisions, never parent companies — a parent owns
  // driver/truck group membership, and removing one would strip access for every
  // admin assigned to it. Refuses while anything still points at the subdivision:
  // BOLs and invoices name the carrier company, and deleting it out from under
  // existing paperwork would leave those records pointing at nothing.
  // ============================================================================
  const handleDeleteSubdivision = async (sub) => {
    if (!canManage || !tenantId || !sub?.id || deletingSubId) return;

    if (!sub.parentCompanyId) {
      alert('Only sub-companies can be deleted here. Deactivate a parent company instead.');
      return;
    }

    const linkedDrivers = drivers.filter(d => d.assignedCompanyId === sub.id);
    const linkedTrucks = trucks.filter(t => t.assignedCompanyId === sub.id);

    // Split the subdivision's loads into live work vs finished history. Only
    // live work blocks a delete — delivered/cancelled loads keep the carrier
    // name on the load itself, so they still render without the company doc.
    let activeLoads = [];
    let historicalLoads = 0;
    setDeletingSubId(sub.id);
    try {
      const loadsSnap = await getDocs(query(
        collection(db, 'loads'),
        where('tenantId', '==', tenantId),
        where('companyId', '==', sub.id)
      ));
      loadsSnap.forEach(loadDoc => {
        const data = loadDoc.data();

        // Deleting or archiving a load never changes its status — it only sets
        // a flag — so a load deleted months ago still reads "Booked". Skip
        // deleted ones entirely and treat archived ones as history.
        if (data.isDeletedByUser === true || data.isDeleted === true) return;

        const status = (data.status || '').toString().trim().toLowerCase();
        if (data.isArchived === true || COMPLETED_LOAD_STATUSES.includes(status)) {
          historicalLoads++;
        } else {
          activeLoads.push({
            id: data.load_id || loadDoc.id,
            status: data.status || '(no status)'
          });
        }
      });
    } catch (error) {
      console.error('Error checking loads for subdivision:', error);
      alert('Could not check whether loads still use this sub-company. Delete cancelled.');
      return;
    }

    if (linkedDrivers.length > 0 || linkedTrucks.length > 0 || activeLoads.length > 0) {
      const nameList = (items, label) =>
        `• ${items.length} ${label}${items.length !== 1 ? 's' : ''}: ` +
        items.slice(0, 5).join(', ') + (items.length > 5 ? ', …' : '') + '\n';

      alert(
        `"${sub.name}" is still in active use, so it can't be deleted:\n\n` +
        (linkedDrivers.length > 0
          ? nameList(linkedDrivers.map(d => d.name || d.id), 'driver')
          : '') +
        (linkedTrucks.length > 0
          ? nameList(linkedTrucks.map(t => t.unitNumber || t.id), 'truck')
          : '') +
        (activeLoads.length > 0
          ? `• ${activeLoads.length} load${activeLoads.length !== 1 ? 's' : ''} not yet delivered:\n` +
            activeLoads.slice(0, 5).map(l => `    – ${l.id} (${l.status})`).join('\n') +
            (activeLoads.length > 5 ? '\n    – …' : '') + '\n'
          : '') +
        `\nMove them to another company first, or use Deactivate to hide this ` +
        `subdivision while keeping its records intact.`
      );
      setDeletingSubId(null);
      return;
    }

    if (!window.confirm(
      `Permanently delete subdivision "${sub.name}"?\n\n` +
      (historicalLoads > 0
        ? `${historicalLoads} completed load${historicalLoads !== 1 ? 's' : ''} in history reference${historicalLoads === 1 ? 's' : ''} this company. ` +
          `They keep their carrier name and stay intact, but reprinting an old BOL or ` +
          `invoice will no longer show this company's address, MC number or logo.\n\n`
        : `Nothing is assigned to it.\n\n`) +
      `This cannot be undone.`
    )) {
      setDeletingSubId(null);
      return;
    }

    try {
      await deleteDoc(doc(db, 'companies', sub.id));

      await logAudit({
        userId: loggedInUser.uid,
        userEmail: loggedInUser.email,
        action: 'COMPANY_DELETED',
        targetType: 'company',
        targetId: sub.id,
        details: {
          name: sub.name,
          type: 'subdivision',
          parentCompanyId: sub.parentCompanyId,
          parentCompanyName: sub.parentCompanyName || null,
          historicalLoadsAffected: historicalLoads
        },
        tenantId
      });

      setActionMessage({
        type: 'success',
        text: `"${sub.name}" was deleted.` +
          (historicalLoads > 0
            ? ` ${historicalLoads} completed load${historicalLoads !== 1 ? 's' : ''} in history kept its carrier name.`
            : '')
      });
    } catch (error) {
      console.error('Error deleting subdivision:', error);
      setActionMessage({ type: 'error', text: `Could not delete "${sub.name}". Nothing was changed.` });
    } finally {
      setDeletingSubId(null);
    }
  };

  // ============================================================================
  // ONE-TIME REPAIR: rebuild company history
  // ----------------------------------------------------------------------------
  // Drivers and trucks moved between parent companies before company history
  // existed lost the link to the company they came from. Their past loads still
  // carry the old parentCompanyId, so the history can be reconstructed from
  // those. Purely additive — never removes or reassigns anything.
  // ============================================================================
  const handleRebuildCompanyHistory = async () => {
    if (!canManage || !tenantId || isRepairingHistory) return;
    if (!window.confirm(
      'Rebuild company history for all drivers and trucks?\n\n' +
      'This reads past loads to work out which companies each driver and truck has ' +
      'worked for, so those companies keep seeing their own history.\n\n' +
      'It only adds history — nothing is removed, reassigned, or deleted.'
    )) return;

    setIsRepairingHistory(true);
    setRepairResult(null);
    try {
      // Which parent companies has each driver/truck actually run loads for?
      const loadsSnap = await getDocs(query(collection(db, 'loads'), where('tenantId', '==', tenantId)));
      const driverCompanies = new Map();
      const truckCompanies = new Map();

      loadsSnap.forEach(loadDoc => {
        const load = loadDoc.data();
        if (!load.parentCompanyId) return;
        if (load.driverId) {
          if (!driverCompanies.has(load.driverId)) driverCompanies.set(load.driverId, new Set());
          driverCompanies.get(load.driverId).add(load.parentCompanyId);
        }
        if (load.truckId) {
          if (!truckCompanies.has(load.truckId)) truckCompanies.set(load.truckId, new Set());
          truckCompanies.get(load.truckId).add(load.parentCompanyId);
        }
      });

      // Merge what we know now with what the loads prove. Only ever grows, so a
      // longer result is the same thing as "this record changed".
      const pending = [];
      const collect = (records, collectionName, fromLoads) => {
        records.forEach(record => {
          const merged = new Set(getCompanyScopeIds(record));
          (fromLoads.get(record.id) || new Set()).forEach(id => merged.add(id));
          const next = Array.from(merged);
          const current = Array.isArray(record.companyHistoryIds) ? record.companyHistoryIds : [];
          if (next.length > 0 && next.length !== current.length) {
            pending.push({ collectionName, id: record.id, companyHistoryIds: next });
          }
        });
      };
      collect(drivers, 'drivers', driverCompanies);
      collect(trucks, 'trucks', truckCompanies);

      // Firestore caps a batch at 500 writes.
      const BATCH_LIMIT = 450;
      for (let i = 0; i < pending.length; i += BATCH_LIMIT) {
        const batch = writeBatch(db);
        pending.slice(i, i + BATCH_LIMIT).forEach(({ collectionName, id, companyHistoryIds }) => {
          batch.update(doc(db, collectionName, id), { companyHistoryIds, updatedAt: serverTimestamp() });
        });
        await batch.commit();
      }

      const driversUpdated = pending.filter(p => p.collectionName === 'drivers').length;
      const trucksUpdated = pending.filter(p => p.collectionName === 'trucks').length;

      if (pending.length > 0) {
        await logAudit({
          userId: loggedInUser.uid,
          userEmail: loggedInUser.email,
          action: 'COMPANY_HISTORY_REBUILT',
          targetType: 'system',
          targetId: tenantId,
          details: { driversUpdated, trucksUpdated, loadsScanned: loadsSnap.size },
          tenantId
        });
      }

      setRepairResult({
        driversUpdated,
        trucksUpdated,
        loadsScanned: loadsSnap.size
      });
    } catch (error) {
      console.error('Error rebuilding company history:', error);
      alert('Failed to rebuild company history: ' + error.message);
    } finally {
      setIsRepairingHistory(false);
    }
  };

  // ============================================================================
  // EXPAND/COLLAPSE
  // ============================================================================
  const toggleExpand = (parentId) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  // ============================================================================
  // ASSIGNMENT MODAL DATA
  // ============================================================================
  const getAssignmentModalProps = () => {
    if (!assignmentType || !assignmentParentCompany) return {};

    const parentId = assignmentParentCompany.id;

    // Build company options: parent company + its subdivisions
    const buildCompanyOptions = () => {
      const options = [
        { id: assignmentParentCompany.id, name: assignmentParentCompany.name, isParent: true }
      ];
      const subs = companies
        .filter(c => c.parentCompanyId === parentId && c.active !== false)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      subs.forEach(sub => {
        options.push({ id: sub.id, name: sub.name, isParent: false });
      });
      return options;
    };

    switch (assignmentType) {
      case 'drivers':
        return {
          allItems: drivers,
          assignedItemIds: drivers.filter(d => d.parentCompanyId === parentId).map(d => d.id),
          title: 'Assign Drivers',
          itemLabelKey: 'name',
          itemSubLabelKey: 'email',
          itemType: 'drivers',
          companyOptions: buildCompanyOptions(),
        };
      case 'trucks':
        return {
          allItems: trucks,
          assignedItemIds: trucks.filter(t => t.parentCompanyId === parentId).map(t => t.id),
          title: 'Assign Trucks',
          itemLabelKey: 'unitNumber',
          itemSubLabelKey: 'yearMake',
          itemType: 'trucks',
          companyOptions: buildCompanyOptions(),
        };
      case 'users':
        // Exclude Super Admins from assignment (they see everything)
        const assignableUsers = users.filter(u => !isSuperAdmin(u));
        return {
          allItems: assignableUsers,
          assignedItemIds: assignableUsers
            .filter(u => (u.assignedParentCompanyIds || []).includes(parentId))
            .map(u => u.id),
          title: 'Assign Users',
          itemLabelKey: 'name',
          itemSubLabelKey: 'email',
          itemType: 'users',
          companyOptions: null, // Users don't need BOL dropdown
        };
      default:
        return {};
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  if (!loggedInUser) {
    return <div className="p-6 text-center text-gray-500">Please log in.</div>;
  }

  const isMainAdminUser = !canManage && (() => {
  const roles = Array.isArray(loggedInUser?.role) ? loggedInUser.role : [loggedInUser?.role].filter(Boolean);
  return roles.includes('Main Admin');
})();

if (!canManage && !isMainAdminUser) {
    return (
      <div className="p-6 bg-red-50 rounded-lg border border-red-200">
        <h3 className="text-lg font-semibold text-red-800">Access Denied</h3>
        <p className="text-red-700 text-sm mt-1">Only Super Admin can manage companies.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading companies...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Company Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            {canManage
              ? 'Set up your companies and decide who can see which drivers, trucks and loads. Assigning someone to a company is what gives them access to its records.'
              : 'These are your companies and who is assigned to each. Only a Super Admin can make changes here.'}
          </p>
        </div>
        {canManage && (
        <button
          onClick={handleOpenAddParent}
          className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md text-sm flex items-center shadow-sm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 mr-2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Company
        </button>
        )}
      </div>

      {/* In-page result of the last action */}
      {actionMessage && (
        <div className={`mb-4 px-4 py-3 rounded-lg border flex items-start justify-between gap-3 ${
          actionMessage.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <p className="text-sm">{actionMessage.text}</p>
          <button
            onClick={() => setActionMessage(null)}
            className="text-current opacity-60 hover:opacity-100 text-sm leading-none"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Unassigned Warning */}
      {(unassignedDrivers.length > 0 || unassignedTrucks.length > 0 || unassignedUsers.length > 0) && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm">
              <p className="font-medium text-yellow-800">Not assigned to any company</p>
              <p className="text-yellow-700 mt-1">
                {/* Joined rather than each part appending its own separator, which
                    used to produce "2 drivers • 3 trucks • not assigned to..." */}
                {[
                  unassignedDrivers.length > 0 && `${unassignedDrivers.length} driver${unassignedDrivers.length !== 1 ? 's' : ''}`,
                  unassignedTrucks.length > 0 && `${unassignedTrucks.length} truck${unassignedTrucks.length !== 1 ? 's' : ''}`,
                  unassignedUsers.length > 0 && `${unassignedUsers.length} user${unassignedUsers.length !== 1 ? 's' : ''}`,
                ].filter(Boolean).join(' • ')}
                {' '}are not assigned to a company yet. Anyone who isn't a Super Admin won't see them.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {parentCompanies.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-gray-500 text-lg">No companies yet</p>
          <p className="text-gray-400 text-sm mt-2">Create a parent company to get started with company-based data isolation.</p>
        </div>
      )}

      {/* Company Cards */}
      <div className="space-y-4">
        {parentCompanies.map(parent => {
          const isExpanded = expandedParents.has(parent.id);
          const subs = getSubdivisions(parent.id);
          const assignedDriversList = getAssignedDrivers(parent.id);
          const assignedTrucksList = getAssignedTrucks(parent.id);
          const assignedUsersList = getAssignedUsers(parent.id);

          return (
            <div key={parent.id} className={`bg-white rounded-lg shadow-sm border ${parent.active ? 'border-gray-200' : 'border-red-200 bg-red-50'}`}>
              {/* Parent Company Header */}
              <div className="px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {/* The whole name block toggles the card — the chevron alone was
                      the only way in, which hid half this screen. */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onClick={() => toggleExpand(parent.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(parent.id); }
                    }}
                    className="flex items-center gap-3 cursor-pointer rounded -m-1 p-1 hover:bg-gray-50"
                  >
                    <span className="text-gray-400 p-1" aria-hidden="true">
                      <svg className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">{parent.name}</h3>
                        {!parent.active && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Inactive</span>
                        )}
                        {parent.type === 'parent' && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Parent</span>
                        )}
                      </div>
                      {parent.address && (
                        <p className="text-xs text-gray-500 mt-0.5">{parent.address}</p>
                      )}
                      <p className="text-xs text-blue-600 mt-1">
                        {isExpanded ? 'Hide details' : 'Show teams, drivers, trucks & users'}
                      </p>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="flex items-center gap-4">
                    {/* Labelled counts. The old row was four unlabelled icons —
                        and the "truck" one was actually a left-right arrows glyph. */}
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {[
                        { label: 'sub-company', plural: 'sub-companies', count: subs.length },
                        { label: 'driver', plural: 'drivers', count: assignedDriversList.length },
                        { label: 'truck', plural: 'trucks', count: assignedTrucksList.length },
                        { label: 'user', plural: 'users', count: assignedUsersList.length },
                      ].map(stat => (
                        <span
                          key={stat.label}
                          className={`px-2 py-1 rounded-full border ${
                            stat.count > 0
                              ? 'bg-gray-50 border-gray-200 text-gray-700'
                              : 'bg-white border-gray-100 text-gray-400'
                          }`}
                        >
                          <span className="font-semibold">{stat.count}</span>{' '}
                          {stat.count === 1 ? stat.label : stat.plural}
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    {canManage && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenEditCompany(parent)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-medium px-2 py-1 rounded hover:bg-indigo-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleCompanyActive(parent)}
                        className={`text-xs font-medium px-2 py-1 rounded ${
                          parent.active
                            ? 'text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50'
                            : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                        }`}
                      >
                        {parent.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-200">
                  {/* Sub-companies — only a trading name for BOLs and invoices,
                      so it is collapsed by default and sits out of the way. */}
                  <details className="px-5 py-3 border-b border-gray-100">
                    <summary className="text-xs font-medium text-gray-500 cursor-pointer select-none">
                      Sub-companies ({subs.length}/{MAX_SUBDIVISIONS})
                      <span className="ml-2 font-normal text-gray-400">
                        — alternative names shown on BOLs and invoices
                      </span>
                    </summary>
                    <div className="pt-3">
                    <div className="flex items-center justify-end mb-3">
                      {subs.length < MAX_SUBDIVISIONS && (
                        <button
                          onClick={() => handleOpenAddSubdivision(parent.id)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add sub-company
                        </button>
                      )}
                    </div>
                    {subs.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No sub-companies yet</p>
                    ) : (
                      <div className="space-y-1">
                        {subs.map(sub => (
                          <SubdivisionRow
                            key={sub.id}
                            sub={sub}
                            onEdit={handleOpenEditCompany}
                            onToggleActive={handleToggleCompanyActive}
                            onDelete={handleDeleteSubdivision}
                            isDeleting={deletingSubId === sub.id}
                            canManage={canManage}
                          />
                        ))}
                      </div>
                    )}
                    </div>
                  </details>

                  <TeamsPanel
                    company={parent}
                    users={users}
                    drivers={drivers}
                    canManage={canManage}
                    busy={teamBusy}
                    onSaveTeams={(nextTeams) => saveTeams(parent, nextTeams)}
                    onAssignPerson={(payload) => assignPersonToTeam(parent, payload)}
                    onLinkDispatcher={linkDriverToDispatcher}
                    onManageDrivers={() => handleOpenAssignment('drivers', parent)}
                    onManageUsers={() => handleOpenAssignment('users', parent)}
                  />

                  {/* Trucks stay separate: they are not people, so they do not
                      belong in the team pool. Collapsed because most visits to
                      this screen are about who works where, not which truck. */}
                  <details className="border-t border-gray-100 px-5 py-3">
                    <summary className="text-xs font-medium text-gray-500 cursor-pointer select-none">
                      Trucks ({assignedTrucksList.length})
                    </summary>
                    <div className="pt-3">
                      {canManage && (
                        <div className="flex justify-end mb-2">
                          <button
                            onClick={() => handleOpenAssignment('trucks', parent)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Manage trucks
                          </button>
                        </div>
                      )}
                      {assignedTrucksList.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No trucks assigned</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {assignedTrucksList.map(t => (
                            <span
                              key={t.id}
                              className="px-2 py-1 text-xs rounded-full bg-white border border-gray-300 text-gray-700"
                            >
                              {t.unitNumber || t.id}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </details>


                  {/* Company Details */}
                  <div className="border-t border-gray-100 px-5 py-3 bg-gray-50 rounded-b-lg">
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                      {parent.phone && <span>📞 {parent.phone}</span>}
                      {parent.email && <span>✉️ {parent.email}</span>}
                      {parent.mcNumber && <span>MC# {parent.mcNumber}</span>}
                      {parent.usdot && <span>USDOT {parent.usdot}</span>}
                      {parent.taxId && <span>Tax ID {parent.taxId}</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ============================================ */}
      {/* COMPANY ADD/EDIT MODAL */}
      {/* ============================================ */}
      {showCompanyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 overflow-y-auto">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl my-8">
            <h3 className="text-xl font-semibold mb-1 text-gray-800">
              {isEditingCompany ? 'Edit' : 'Add'} {companyModalType === 'subdivision' ? 'Sub-company' : 'Company'}
            </h3>
            {companyModalType === 'subdivision' && subdivisionParentId && (
              <p className="text-sm text-gray-500 mb-4">
                Under: <span className="font-medium text-blue-700">
                  {companies.find(c => c.id === subdivisionParentId)?.name}
                </span>
              </p>
            )}
            {companyModalType === 'parent' && !isEditingCompany && (
              <p className="text-sm text-gray-500 mb-4">
                Parent companies control data visibility. Assign drivers, trucks, and users after creation.
              </p>
            )}

            <form onSubmit={handleCompanyFormSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={companyForm.name}
                    onChange={handleCompanyFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                    placeholder="e.g., Gold Star Delivery"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={companyForm.address}
                    onChange={handleCompanyFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                    placeholder="Full address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={companyForm.phone}
                    onChange={handleCompanyFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={companyForm.email}
                    onChange={handleCompanyFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MC Number</label>
                  <input
                    type="text"
                    name="mcNumber"
                    value={companyForm.mcNumber}
                    onChange={handleCompanyFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">USDOT</label>
                  <input
                    type="text"
                    name="usdot"
                    value={companyForm.usdot}
                    onChange={handleCompanyFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                  <input
                    type="text"
                    name="taxId"
                    value={companyForm.taxId}
                    onChange={handleCompanyFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 mt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCompanyModal(false);
                    setCompanyForm(initialCompanyState);
                  }}
                  disabled={isProcessing}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  {isProcessing
                    ? (isEditingCompany ? 'Updating...' : 'Creating...')
                    : (isEditingCompany ? 'Save Changes' : `Create ${companyModalType === 'subdivision' ? 'sub-company' : 'company'}`)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ASSIGNMENT MODAL */}
      {/* ============================================ */}

      {/* Maintenance — a one-time repair tool, kept out of the main flow */}
      {canManage && (
        <details className="mt-8 border border-gray-200 rounded-lg bg-gray-50">
          <summary className="px-4 py-3 text-sm font-medium text-gray-700 cursor-pointer select-none">
            Maintenance
          </summary>
          <div className="px-4 pb-4 pt-1 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="text-sm">
                <p className="font-medium text-gray-800">Rebuild driver &amp; truck company history</p>
                <p className="text-gray-600 mt-1">
                  Drivers and trucks stay visible to every company they have worked for, even after
                  moving elsewhere. Run this once if someone was moved before that was introduced —
                  it reads past loads to work out where they used to be. It only adds history and
                  never removes or reassigns anything.
                </p>
                {repairResult && (
                  <p className="text-green-700 mt-2 font-medium">
                    {repairResult.driversUpdated === 0 && repairResult.trucksUpdated === 0
                      ? `Everything was already up to date — nothing needed fixing (checked ${repairResult.loadsScanned} load${repairResult.loadsScanned !== 1 ? 's' : ''}).`
                      : `Repaired ${repairResult.driversUpdated} driver${repairResult.driversUpdated !== 1 ? 's' : ''} and ${repairResult.trucksUpdated} truck${repairResult.trucksUpdated !== 1 ? 's' : ''}, using ${repairResult.loadsScanned} past load${repairResult.loadsScanned !== 1 ? 's' : ''}.`}
                  </p>
                )}
              </div>
              <button
                onClick={handleRebuildCompanyHistory}
                disabled={isRepairingHistory}
                className="bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md text-sm whitespace-nowrap shadow-sm"
              >
                {isRepairingHistory ? 'Rebuilding...' : 'Rebuild history'}
              </button>
            </div>
          </div>
        </details>
      )}

      <AssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        parentCompany={assignmentParentCompany}
        onSave={handleSaveAssignment}
        isSaving={isSavingAssignment}
        {...getAssignmentModalProps()}
      />

      {/* ============================================ */}
      {/* INFO NOTE */}
      {/* ============================================ */}
      <div className="mt-6 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-200">
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="font-medium text-gray-600">How company isolation works:</p>
          <p className="mt-1">
            Non-Super Admin users only see loads, drivers, and trucks belonging to their assigned parent company.
            Super Admin always sees everything across all companies.
            Subdivisions are used for BOL/invoice branding — data filtering is based on the parent company level.
          </p>
          <p className="mt-1">
            <span className="font-medium">Existing companies</span> without a type are treated as parent companies.
            You can add the <span className="font-medium">type: "parent"</span> field to them for clarity, but it's not required.
          </p>
        </div>
      </div>
    </div>
  );
}